import { Link } from "react-router-dom";
import {
    HouseholdTrackingEligibility,
    HouseholdTrackingGroup
} from "../../models/pageModels/HouseholdTracking";
import Checkbox from "../UI/Checkbox";
import Loader from "../UI/Loader";
import "./HouseholdTracking.css";

interface HouseholdTrackingReviewProps {
    groups: HouseholdTrackingGroup[];
    eligibility: Record<string, HouseholdTrackingEligibility>;
    loading: boolean;
    programName: string;
    selectedClientIds: string[];
    disabled?: boolean;
    onSelectedClientIdsChange: (clientIds: string[]) => void;
}

const HouseholdTrackingReview = (props: HouseholdTrackingReviewProps) => {
    const getEligibility = (clientId?: string): HouseholdTrackingEligibility => {
        if (!clientId) {
            return {
                status: 'ineligible',
                reason: 'Not linked to a participant profile'
            };
        }

        return props.eligibility[clientId] || {
            status: 'checking'
        };
    };

    const updateSelectedClient = (clientId: string, selected: boolean) => {
        if (props.disabled) {
            return;
        }

        const selectedClientIds = new Set(props.selectedClientIds);
        if (selected) {
            selectedClientIds.add(clientId);
        } else {
            selectedClientIds.delete(clientId);
        }
        props.onSelectedClientIdsChange(Array.from(selectedClientIds));
    };

    return (
        <div id="household-tracking-review" className="household-tracking-card margin-top-4">
            <div className="household-tracking-card-header">
                <h4>Household Submission Review</h4>
                <p>
                    Everyone is shown below. Choose which eligible household participants enrolled in {props.programName} should receive this form.
                </p>
            </div>
            {props.loading && <div className="household-tracking-loading">
                <Loader noMarginTop={true} />
                <span>Checking household participant eligibility...</span>
            </div>}
            {props.groups.map(group => (
                <div className="household-tracking-group" key={group.key}>
                    <h5>{group.name}</h5>
                    <div className="household-tracking-table-container">
                        <table className="household-tracking-table">
                            <thead>
                                <tr>
                                    <th>Submit</th>
                                    <th>Name</th>
                                    <th>Relationship</th>
                                    <th>Eligibility</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {group.people.map(person => {
                                    const personEligibility = getEligibility(person.clientId);
                                    const reason = person.duplicateSubmission
                                        ? 'Duplicate participant; submitted once'
                                        : personEligibility.reason;
                                    const statusLabel = personEligibility.status === 'eligible'
                                        ? 'Eligible'
                                        : personEligibility.status === 'ineligible'
                                            ? 'Not Eligible'
                                            : 'Checking';
                                    const canSelect = !!person.clientId
                                        && !person.duplicateSubmission
                                        && personEligibility.status === 'eligible';
                                    const isSelected = !!person.clientId && props.selectedClientIds.includes(person.clientId);
                                    const submitLabel = person.duplicateSubmission
                                        ? 'Duplicate'
                                        : personEligibility.status === 'checking'
                                            ? 'Checking'
                                            : canSelect
                                                ? undefined
                                                : 'Unavailable';

                                    return (
                                        <tr key={person.key}>
                                            <td data-label="Submit">
                                                {canSelect
                                                    ? <Checkbox
                                                        id={`household-submit-${person.key}`}
                                                        checked={isSelected}
                                                        disabled={props.disabled}
                                                        label={`Submit for ${person.name}`}
                                                        labelClassName="household-tracking-select-label"
                                                        containerClassName="unboxed household-tracking-select"
                                                        onChange={checked => updateSelectedClient(person.clientId!, checked)} />
                                                    : <span className="text-sub">{submitLabel}</span>}
                                            </td>
                                            <td data-label="Name">
                                                <div className="text-bold">
                                                    {person.clientId
                                                        ? <Link to={`/participants/${person.clientId}/profile-demographics`} target="_blank">{person.name}</Link>
                                                        : person.name}
                                                </div>
                                                {person.isPrimary && <small>Current participant</small>}
                                            </td>
                                            <td data-label="Relationship">{person.relationship}</td>
                                            <td data-label="Eligibility">
                                                <div className={`household-tracking-status ${personEligibility.status}`}>
                                                    {statusLabel}
                                                </div>
                                            </td>
                                            <td data-label="Details">
                                                {reason
                                                    ? <span className="household-tracking-reason">{reason}</span>
                                                    : <span className="text-sub">-</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default HouseholdTrackingReview;
